import {cn} from '@containers/html-utils'
import {format, getHours} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Time} from 'yougile-util'
import {Shift, TaskWithColor, useCalendarStore} from '../../calendar-store'
import {roundedTo15Min, validDateFormat} from '../../utils'
import {AddTaskPopup} from '../add-task/add-task-popup'
import {useDraggableStore} from '../draggable/draggable-store'
import {TimeTw} from '../task/task-content'
import {TasksMapping} from '../task/task-mapping'
import {GreyContent} from './hour-grey-content'
import {HourLine} from './hour-line'
import {HourPlaceholder} from './hour-placeholder'

const HourTw = tw.div`
  relative
  h-60
  border-t-0
  border-b-1
  border-l-0
  border-solid
  grow-0
  basis-1/7
  w-1/7
  border-r-1
  min-w-200
  border-input-disabled
`

interface Props {
  day: number
  shift: Shift
  currentHour: number
  currentDate: Date
  tasksWithStartDate: TaskWithColor[]
  tasksWithoutStartDate: TaskWithColor[]
  start: Date
  end: Date
}

export const Hour = observer(
  // eslint-disable-next-line max-lines-per-function
  ({
    day,
    shift,
    currentHour,
    currentDate,
    tasksWithStartDate,
    tasksWithoutStartDate,
    start,
    end
  }: Props) => {
    const [state, store] = useCalendarStore()
    const [draggableState, draggableStore] = useDraggableStore()
    const [hourTime] = store.currentHourAndMinute

    const onMouseMove = () => {
      if (
        !state.showPopupButton.value ||
        state.showPopupButton.hour !== start
      ) {
        store.showedPopupButton(true, start)
      }
      if (draggableState.hoveredItemId !== draggableState.draggedItem.id) {
        draggableStore.hoveredItemId(null)
        draggableStore.showedDrag(false)
      }
    }

    const onMouseLeave = () => {
      store.showedPopupButton(false, null)
    }

    return (
      <HourTw
        data-hour={start}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        onClick={() => {
          store.setOpenedPopup(true)
        }}
        style={{
          backgroundColor: day > 4 && '#FB5D5D0D'
        }}
        className={cn(getHours(start) === 0 && 'border-t-1')}
      >
        {shift === 'current' && (
          <HourPlaceholder
            start={start}
            end={end}
            currentDate={currentDate}
            currentHour={currentHour}
          />
        )}

        <AddTaskPopup start={start} end={end} />

        <GreyContent
          start={start}
          end={end}
          currentDate={currentDate}
          currentHour={currentHour}
        />

        {currentHour === hourTime && <HourLine day={day} />}

        {tasksWithStartDate.map(task => (
          <TasksMapping
            task={task}
            key={task.task.id}
            currentHour={currentHour}
            currentDate={currentDate}
            start={start}
            end={end}
            showAllDayWithStartDateTask={state.showAllDayWithStartDateTask}
            currentTime={state.currentTime}
          />
        ))}
        {tasksWithoutStartDate.map(task => (
          <TasksMapping
            task={task}
            key={task.task.id}
            currentHour={currentHour}
            currentDate={currentDate}
            start={start}
            end={end}
            withoutStart
            showAllDayWithStartDateTask={state.showAllDayWithStartDateTask}
            currentTime={state.currentTime}
          />
        ))}
      </HourTw>
    )
  }
)

export const ShowDraggedTime = observer(() => {
  const [draggableState] = useDraggableStore()

  return draggableState.draggedItem.taskType === 'without-start-date' ? (
    <TimeTw>
      {draggableState.startDate && draggableState.deadline && (
        <>{format(draggableState.startDate, 'HH:mm')}-</>
      )}
      {format(
        roundedTo15Min(
          Time.toLocalDate(
            Time.toTimestamp(draggableState.deadline) + 15 * Time.minute
          )
        ),
        'HH:mm'
      )}
    </TimeTw>
  ) : (
    <TimeTw>
      {validDateFormat(draggableState.startDate, 'HH:mm')}-
      {validDateFormat(draggableState.deadline, 'HH:mm')}
    </TimeTw>
  )
})
